import { useState, useEffect, useMemo, useRef } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import Logo from "assets/images/1024.png";

import AuthenticRoute from "./AuthenticRoute.js";
import SosAlertModal from "components/SosAlertModal";
import { socket, connectSocket, disconnectSocket } from "components/socket/socket";
import apiRequest from "./apiRequest";
import { endPoints } from "./apis";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const [sosModalOpen, setSosModalOpen] = useState(false);
  const [sosPayload, setSosPayload] = useState(null);
  const sosAudioRef = useRef(null);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    connectSocket();

    const handleOrderSos = (payload) => {
      if (!payload) return;
      setSosPayload(payload);
      setSosModalOpen(true);
    };

    socket.on("orderSos", handleOrderSos);

    return () => {
      socket.off("orderSos", handleOrderSos);
      disconnectSocket();
    };
  }, []);

  useEffect(() => {
    if (sosAudioRef.current) return;
    sosAudioRef.current = new Audio("/sounds/bill.wav");
    sosAudioRef.current.preload = "auto";
    sosAudioRef.current.loop = true;

    const unlockAudio = () => {
      const audio = sosAudioRef.current;
      if (!audio) return;
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        })
        .catch(() => {});
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
    };

    window.addEventListener("click", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });
    window.addEventListener("touchstart", unlockAudio, { once: true });
  }, []);

  useEffect(() => {
    const audio = sosAudioRef.current;
    if (!audio) return;
    if (sosModalOpen) {
      audio.currentTime = 0;
      audio.loop = true;
      audio.play().catch((err) => {
        console.warn("SOS audio play blocked until user interaction:", err);
      });
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [sosModalOpen]);

  const handleSosRespond = async () => {
    const sosId = sosPayload?.sos?.id;
    try {
      if (sosId) {
        await apiRequest.put(`${endPoints.UPDATE_SOS_REQUEST_STATUS}/${sosId}/status`, {
          status: "resolved",
        });
      }
    } catch (err) {
      console.error("Failed to resolve SOS request:", err);
    } finally {
      setSosModalOpen(false);
      setSosPayload(null);
    }
  };

  const handleSosBlock = () => {
    setSosModalOpen(false);
    setSosPayload(null);
  };

  const handleSosShowLocation = () => {
    const latitude = sosPayload?.sos?.latitude ?? sosPayload?.sos?.location?.latitude;
    const longitude = sosPayload?.sos?.longitude ?? sosPayload?.sos?.location?.longitude;
    if (!latitude || !longitude) return;
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        if (route.isPrivate) {
          return (
            <Route
              key={route.key}
              path={route.route}
              element={<AuthenticRoute>{route.component}</AuthenticRoute>}
            />
          );
        }

        return <Route key={route.key} path={route.route} element={route.component} />;
      }

      return null;
    });

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            // brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brand={Logo}
            // brandName=""
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
      <SosAlertModal
        open={sosModalOpen}
        data={sosPayload}
        onRespond={handleSosRespond}
        onBlock={handleSosBlock}
        onShowLocation={handleSosShowLocation}
      />
    </ThemeProvider>
  );
}
